/**
 * Production optimization utilities for enterprise deployment
 */

/**
 * Initialize production performance optimizations
 */
export function initializeProductionOptimizations() {
	// Disable React DevTools in production
	if (import.meta.env.PROD && typeof window !== 'undefined') {
		if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
			window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = null;
			window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberUnmount = null;
		}
	}

	// Set up error boundary for unhandled promise rejections
	window.addEventListener('unhandledrejection', event => {
		console.error('Unhandled promise rejection:', event.reason);

		// Report to error tracking
		if (typeof window !== 'undefined' && 'Sentry' in window) {
			(window as any).Sentry.captureException(event.reason);
		}
	});

	// Set up global error handler
	window.addEventListener('error', event => {
		console.error('Global error:', event.error);

		// Report to error tracking
		if (typeof window !== 'undefined' && 'Sentry' in window) {
			(window as any).Sentry.captureException(event.error);
		}
	});

	// Optimize performance observers for production
	if ('PerformanceObserver' in window) {
		try {
			const observer = new PerformanceObserver(list => {
				const entries = list.getEntries();
				entries.forEach(entry => {
					// Only log critical performance issues
					if (entry.entryType === 'measure' && entry.duration > 1000) {
						console.warn(
							'Long task detected:',
							entry.name,
							`${entry.duration.toFixed(1)}ms`
						);
					}
				});
			});

			observer.observe({ entryTypes: ['measure', 'navigation'] });
		} catch (error) {
			// Silent failure if PerformanceObserver not supported
		}
	}

	console.log('Production optimizations initialized');
}

/**
 * Clean up resources before page unload
 */
export function setupCleanupOnUnload() {
	window.addEventListener('beforeunload', () => {
		// Cancel any pending requests
		if ('AbortController' in window) {
			// This would be handled by individual components
		}

		// Clear temporary data
		try {
			const keys = Object.keys(sessionStorage);
			keys.forEach(key => {
				if (key.startsWith('temp_') || key.startsWith('cache_')) {
					sessionStorage.removeItem(key);
				}
			});
		} catch (error) {
			// Silent failure
		}
	});
}

/**
 * Set up production-grade console logging
 */
export function setupProductionLogging() {
	if (import.meta.env.PROD) {
		// Disable console.log in production, keep warn and error
		console.log = () => {};

		// Override console methods to include timestamp and context
		const originalWarn = console.warn;
		const originalError = console.error;

		console.warn = (...args) => {
			originalWarn(`[${new Date().toISOString()}] [WARN]`, ...args);
		};

		console.error = (...args) => {
			originalError(`[${new Date().toISOString()}] [ERROR]`, ...args);
		};
	}
}

/**
 * Initialize all production optimizations
 */
export function initializeEnterprise() {
	initializeProductionOptimizations();
	setupCleanupOnUnload();
	setupProductionLogging();

	console.info('Enterprise optimizations initialized');
}
